import React, { useEffect, useContext, useState } from "react";
import PropTypes from "prop-types";
import Video from "twilio-video";
import { RoomContext } from "../../context/RoomContext";
import { MediaContext } from "../../context/MediaContext";
import Participant from "../RoomParticipant/Participant.jsx";
import VideoGrid from "../VideoGrid/VideoGrid";
import Box from "@mui/material/Box";
import UserVideo from "../UserVideo/UserVideo.jsx";
import CSS from "../Lobby/Lobby.module.css";
import "./VideoStyles.module.css";
import Loading from "../Loading/Loading.jsx";

const Room = ({ roomName, roomToken, deviceType }) => {
  const {
    room,
    updateRoom,
    updateRoomName,
    updatePartnerName,
    updateAlreadyFriends,
  } = useContext(RoomContext);

  const { localAudioTrack, localVideoTrack } = useContext(MediaContext);

  const [remoteVideoEnabled, setRemoteVideoEnabled] = useState(true);
  const [remoteAudioEnabled, setRemoteAudioEnabled] = useState(true);

  const ellipsisLoader = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1,
        textAlign: "center",
        px: 2,
      }}
    >
      <span className="vu-dominoLoader" aria-label="Loading" role="status">
        <span className="vu-domino" />
        <span className="vu-domino" />
        <span className="vu-domino" />
      </span>
    </Box>
  );

  // Remote enabled/disabled state listeners (unchanged logic, just left here)
  useEffect(() => {
    if (!room) return;

    const handleVideoDisabled = (track) => track.kind === "video" && setRemoteVideoEnabled(false);
    const handleVideoEnabled = (track) => track.kind === "video" && setRemoteVideoEnabled(true);
    const handleAudioDisabled = (track) => track.kind === "audio" && setRemoteAudioEnabled(false);
    const handleAudioEnabled = (track) => track.kind === "audio" && setRemoteAudioEnabled(true);

    const handleTrackSubscribed = (track) => {
      if (track.kind === "video") {
        track.on("disabled", handleVideoDisabled);
        track.on("enabled", handleVideoEnabled);
        track.isEnabled ? handleVideoEnabled(track) : handleVideoDisabled(track);
      }
      if (track.kind === "audio") {
        track.on("disabled", handleAudioDisabled);
        track.on("enabled", handleAudioEnabled);
        track.isEnabled ? handleAudioEnabled(track) : handleAudioDisabled(track);
      }
    };

    const handleTrackUnsubscribed = (track) => {
      if (track.kind === "video") {
        track.off("disabled", handleVideoDisabled);
        track.off("enabled", handleVideoEnabled);
      }
      if (track.kind === "audio") {
        track.off("disabled", handleAudioDisabled);
        track.off("enabled", handleAudioEnabled);
      }
    };

    const handleParticipantConnected = (participant) => {
      participant.tracks.forEach((pub) => {
        if (pub.isSubscribed) handleTrackSubscribed(pub.track);
      });
      participant.on("trackSubscribed", handleTrackSubscribed);
      participant.on("trackUnsubscribed", handleTrackUnsubscribed);
    };

    room.participants.forEach(handleParticipantConnected);
    room.on("participantConnected", handleParticipantConnected);

    return () => {
      room.participants.forEach((p) => {
        p.off("trackSubscribed", handleTrackSubscribed);
        p.off("trackUnsubscribed", handleTrackUnsubscribed);
      });
      room.off("participantConnected", handleParticipantConnected);
    };
  }, [room]);

  // ✅ Connect ONCE using existing LocalTracks (no toggle deps)
  useEffect(() => {
    let currentRoom = null;
    let cancelled = false;

    if (!roomName || !roomToken) return;

    // Wait until we at least have *something* to publish (audio or video)
    // If you require both, change this condition.
    if (!localAudioTrack && !localVideoTrack) return;

    const tracks = [localAudioTrack, localVideoTrack].filter(Boolean);

    Video.connect(roomToken, {
      name: roomName,
      tracks,
    })
      .then((joined) => {
        if (cancelled) {
          joined.disconnect();
          return;
        }
        currentRoom = joined;
        updateRoom(joined);
        updateRoomName(roomName);
      })
      .catch((e) => {
        console.error("[Room] connect failed:", e);
      });

    return () => {
      cancelled = true;

      try {
        if (currentRoom) currentRoom.disconnect();
        if (currentRoom && typeof currentRoom.destroy === "function") currentRoom.destroy();
      } catch (_) {}

      updateRoom(null);
      updateRoomName(null);
      updatePartnerName(null);
      updateAlreadyFriends(false);
    };
  }, [
    roomName,
    roomToken,
    localAudioTrack,
    localVideoTrack,
    updateRoom,
    updateRoomName,
    updatePartnerName,
    updateAlreadyFriends,
  ]);

  const remoteParticipant = room ? Array.from(room.participants.values())[0] : null;

  return (
    <>
      <style>
        {`
          .vu-dominoLoader { display:inline-flex; align-items:flex-end; gap:10px; height:22px; user-select:none; }
          .vu-domino { width:6px; height:18px; border-radius:999px; background: rgba(249, 250, 251, 0.85);
            transform: translateY(0); opacity:0.55;
            animation: vuDominoFall 900ms infinite cubic-bezier(0.2, 0.75, 0.2, 1);
            will-change: transform, opacity;
          }
          .vu-domino:nth-child(2) { animation-delay:120ms; }
          .vu-domino:nth-child(3) { animation-delay:240ms; }
          @keyframes vuDominoFall {
            0% { transform: translateY(0); opacity:0.55; }
            18% { transform: translateY(10px); opacity:1; }
            45% { transform: translateY(10px); opacity:0.95; }
            72% { transform: translateY(0); opacity:0.65; }
            100% { transform: translateY(0); opacity:0.55; }
          }
        `}
      </style>

      {room && remoteParticipant ? (
        <VideoGrid>
          {remoteVideoEnabled ? (
            <Participant
              key={remoteParticipant.sid}
              participant={remoteParticipant}
              type="remote"
              remoteAudioEnabled={remoteAudioEnabled}
            />
          ) : (
            <Box sx={{ position: "relative", width: "100%", height: "100%", overflow: "hidden" }}>
              <Participant
                key={remoteParticipant.sid}
                participant={remoteParticipant}
                type="remote"
                remoteAudioEnabled={remoteAudioEnabled}
              />
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  backgroundColor: "black",
                  zIndex: 1,
                }}
              />
            </Box>
          )}

          <Participant
            key={room.localParticipant.sid}
            participant={room.localParticipant}
            type="local"
            remoteAudioEnabled={remoteAudioEnabled}
          />
        </VideoGrid>
      ) : (
        <VideoGrid>
          <Box position="relative" width="100%" height="100%" overflow="hidden">
            <UserVideo />
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                backgroundColor: "#212529",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1,
              }}
            >
              {ellipsisLoader}
            </Box>
          </Box>
          <UserVideo />
        </VideoGrid>
      )}
    </>
  );
};

Room.propTypes = {
  roomName: PropTypes.string.isRequired,
  roomToken: PropTypes.string.isRequired,
  deviceType: PropTypes.string.isRequired,
};

export default Room;
